/**
 * OrbitalDebrisPassiveSkillEffect — Astral dense-path passive: orbiting debris
 * deals contact damage to nearby enemies while Collapse is active.
 *
 * When this passive unlocks, it watches the Collapse skill's active state.
 * On Collapse activation, OrbitalDebrisSystem.startAccumulating() begins
 * filling the orbit (0 to 5 objects over 10 seconds at base rate). On
 * deactivation, OrbitalDebrisSystem.clear() wipes the orbit.
 *
 * While the orbit is accumulating, a contact check runs every 1000ms.
 * Each enemy within orbitRadiusTiles of the player takes
 * `debrisDamagePerContact * count` damage — one hit per orbiting object.
 *
 * Fork 2 gate: this passive unlocks the Dense path's fork choice between
 * Meteo and Slingshot. The fork gate logic lives in SkillProgressionComponent.
 *
 * Slingshot dependency: OrbitalDebrisSystem.count is the ammo counter for
 * the Slingshot skill (consumeAll on fire).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § dense_path_passive
 * @see design-decisions.md § Astral Base Values (orbit fill 10s)
 * @see ADR-064 (attunement shared systems architecture)
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { OrbitalDebrisSystem } from '../../../systems/OrbitalDebrisSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface OrbitalDebrisPassiveDeps {
  /** Collapse skill effect — queried for active state each frame. */
  readonly collapseSource: {
    isActive(): boolean;
  };
  /** Shared debris system — accumulation, count, and lifecycle. */
  readonly debrisSystem: OrbitalDebrisSystem;
  /** Player position for orbit center (contact damage radius check). */
  readonly player: {
    getPosition(): { x: number; y: number };
  };
  /** Enemy proximity queries. */
  readonly enemies: {
    getEnemiesInRadius(x: number, y: number, radiusTiles: number): string[];
  };
  /** Direct damage application. */
  readonly combat: {
    dealDamage(enemyId: string, damage: number): void;
  };
  /**
   * Upgrade effect resolver — optional. When present, queries
   * orbitAccumulationMultiplier each frame to feed OrbitalDebrisSystem.
   * Wired by Rapid Orbit (slingshot_upgrade_1).
   */
  readonly resolver?: {
    getMultiplicativeEffect(skillId: string, key: string, base: number): number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface OrbitalDebrisPassiveConfig {
  /** Damage per debris object per contact check (3 from JSON). */
  readonly debrisDamagePerContact: number;
  /** Interval between contact damage checks in ms. */
  readonly contactCheckIntervalMs: number;
  /** Orbit radius in tiles — enemies within this radius of the player take contact damage. */
  readonly orbitRadiusTiles: number;
}

const DEFAULT_CONFIG: OrbitalDebrisPassiveConfig = {
  debrisDamagePerContact: 3,
  contactCheckIntervalMs: 1000,
  orbitRadiusTiles: 1.5,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class OrbitalDebrisPassiveSkillEffect implements SkillEffect {
  readonly skillId = 'dense_path_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: OrbitalDebrisPassiveDeps;
  readonly config: OrbitalDebrisPassiveConfig;

  /** Edge detection — was Collapse active on the previous frame? */
  private _wasCollapseActive = false;
  /** Accumulator for contact damage check interval. */
  private _contactAccumulator = 0;

  // --- Telemetry ---

  /** Total contact damage events (one event = all enemies hit in one check). */
  private _contactChecks = 0;
  /** Total individual enemy hits across all contact checks. */
  private _totalEnemyHits = 0;
  /** Total damage dealt across all contact checks. */
  private _totalDamageDealt = 0;
  /** Number of Collapse activation cycles observed. */
  private _activationCycles = 0;

  constructor(deps: OrbitalDebrisPassiveDeps, config: Partial<OrbitalDebrisPassiveConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get contactChecks(): number { return this._contactChecks; }
  get totalEnemyHits(): number { return this._totalEnemyHits; }
  get totalDamageDealt(): number { return this._totalDamageDealt; }
  get activationCycles(): number { return this._activationCycles; }
  get wasCollapseActive(): boolean { return this._wasCollapseActive; }

  // ---------------------------------------------------------------------------
  // SkillEffect interface
  // ---------------------------------------------------------------------------

  activate(): boolean { return false; }
  isActive(): boolean { return false; }

  update(delta: number): void {
    const collapseActive = this.deps.collapseSource.isActive();

    // --- Edge detection: Collapse just activated ---
    if (collapseActive && !this._wasCollapseActive) {
      this.deps.debrisSystem.startAccumulating();
      this._contactAccumulator = 0;
      this._activationCycles++;
    }

    // --- Edge detection: Collapse just deactivated ---
    if (!collapseActive && this._wasCollapseActive) {
      this.deps.debrisSystem.clear();
      this._contactAccumulator = 0;
    }

    this._wasCollapseActive = collapseActive;

    // --- Per-frame: tick debris system and contact damage ---
    if (collapseActive) {
      // Rapid Orbit (slingshot_upgrade_1): push accumulation multiplier each frame.
      if (this.deps.resolver) {
        const mult = this.deps.resolver.getMultiplicativeEffect(
          'slingshot', 'orbitAccumulationMultiplier', 1.0,
        );
        this.deps.debrisSystem.setAccumulationMultiplier(mult);
      }
      this.deps.debrisSystem.update(delta);
      this._tickContactDamage(delta);
    }
  }

  destroy(): void {
    // If Collapse was active when destroyed, clean up the debris system.
    if (this._wasCollapseActive) {
      this.deps.debrisSystem.clear();
    }
    this._wasCollapseActive = false;
    this._contactAccumulator = 0;
  }

  // ---------------------------------------------------------------------------
  // Contact damage
  // ---------------------------------------------------------------------------

  private _tickContactDamage(delta: number): void {
    this._contactAccumulator += delta;
    const interval = this.config.contactCheckIntervalMs;

    while (this._contactAccumulator >= interval) {
      this._contactAccumulator -= interval;

      // Count checked at fire time — accumulator always advances so the
      // first check after debris appears isn't delayed.
      const count = this.deps.debrisSystem.count;
      if (count <= 0) continue;

      this._contactChecks++;

      const pos = this.deps.player.getPosition();
      const targets = this.deps.enemies.getEnemiesInRadius(
        pos.x, pos.y, this.config.orbitRadiusTiles,
      );

      const damagePerEnemy = this.config.debrisDamagePerContact * count;

      for (const enemyId of targets) {
        this.deps.combat.dealDamage(enemyId, damagePerEnemy);
        this._totalEnemyHits++;
        this._totalDamageDealt += damagePerEnemy;
      }
    }
  }
}
